<script setup lang="ts">
import { computed } from 'vue';
import useContext from '@/composables/useContext';
import useWebsiteTexts from '@/composables/useWebsiteTexts';

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
});

const { websiteText } = useWebsiteTexts();
const { formatPrice } = useContext();
const giftCardFees = computed(() => {
  return props.item.giftCardFees?.map(f => {
    if (f.ignoreQuantity) return f;

    const pricePerItemString = `(${formatPrice(f.value)}/${websiteText('productdetailspage_printpieceabbreviated').value})`;

    return {
      ...f,
      priceString: pricePerItemString,
    };
  }) || [];
});

const unitPrice = computed(() => {
  if (!props.item.originalPrice) return props.item.currentPrice;
  return props.item.currentPrice < props.item.originalPrice
    ? props.item.currentPrice
    : props.item.originalPrice;
});

const itemTotalPrice = computed(() => {
  return props.item.totalPrice;
});

const quantityText = computed(() => {
  return `${props.item.quantity} ${websiteText('productdetailspage_printpieceabbreviated').value}`;
});

const pricePerItemText = computed(() => {
  return `(${formatPrice(unitPrice.value)}/${websiteText('productdetailspage_printpieceabbreviated').value})`;
});
</script>
<template>
  <div class="cart-gift-card-price-item">
    <div class="cart-gift-card-price-item__product-name-and-number">
      <div class="cart-gift-card-price-item__product-name">
        {{ websiteText('giftcard__giftcard').value }} - {{ item.variantName }} {{ item.giftCardName }}
      </div>
      <div v-if="item.articleNumber" class="cart-gift-card-price-item__details-spacer"></div>
      <div class="cart-gift-card-price-item__article-number">
        {{ item.articleNumber }}
      </div>
    </div>
    <div class="cart-gift-card-price-item__quantity-and-price">
      <div class="cart-gift-card-price-item__quantity">
        {{quantityText}} {{pricePerItemText}}
      </div>
      <div class="cart-gift-card-price-item__price">
        {{ formatPrice(itemTotalPrice) }}
      </div>
    </div>
    <div
      v-for="(fee, idx) in giftCardFees"
      :key="`${fee.text}${fee.totalRow}${idx}`"
      class="cart-gift-card-price-item__fee"
    >
      <div class="cart-gift-card-price-item__fee-quantity">
        {{ fee.text }} {{ fee.priceString }}
      </div>
      <div class="cart-gift-card-price-item__fee-price">
        {{ formatPrice(fee.totalRow) }}
      </div>
    </div>
  </div>
</template>
<style>
.cart-gift-card-price-item {
  padding-left: 10px;
  color: var(--color-neutrals-80);
  font-size: 12px;
  line-height: 17px;
}

.checkout-order-summary__row-content .cart-gift-card-price-item {
  display: grid;
  gap: 2px;
  padding-bottom: 12px;
  margin-top: -6px;
}

.cart-gift-card-price-item__product-name-and-number {
  font-size: 12px;
  letter-spacing: .18px;
  line-height: 17px;
  display: flex;
  align-items: center;
  color: var(--color-text-darker);
}

.cart-gift-card-price-item__product-name {
  font-weight: 700;
}

.cart-gift-card-price-item__details-spacer {
  margin: 0 6px;
  height: 12px;
  border-left: 1px solid #e5e5e5;
}

.cart-gift-card-price-item__quantity-and-price,
.cart-gift-card-price-item__fee {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2px;
  padding-left: 10px;
}

.cart-gift-card-price-item__price,
.cart-gift-card-price-item__fee-price {
  color: var(--color-text-darker);
}
</style>
